import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import moment from 'moment';
import { openSnackbarMain } from 'components/Shared/openSnackbarMain';
import { formatarDocumento } from 'components/Utils/documentoAlfanumerico';
import {
  capturarTitulo,
  capturarTitulosEmLote,
  devolverTitulo,
  getConciliacaoFlowTitulosBancarizador,
  getConciliacaoFlowTitulosBase,
  getParceirosConciliacao
} from 'api/conciliacaoFlow';
import { statusChip } from './ConciliacaoFlowModelParts';

export const initialFilters = {
  parceiros: [],
  dataDe: moment().subtract(30, 'days').format('YYYY-MM-DD'),
  dataAte: '',
  liquidacaoDe: '',
  liquidacaoAte: '',
  type: '',
  bordero: '',
  ndoc: '',
  vencimentoParceiroDe: '',
  vencimentoParceiroAte: '',
  vencimentoBancarizadorDe: '',
  vencimentoBancarizadorAte: '',
  statusFlow: '',
  statusTransferencia: '',
  statusTransfer: '',
  situacao: ''
};

const money = value => Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const date = value => (value ? moment(value).format('DD/MM/YYYY') : '—');
const rowDate = (row, field) => date(row[field]);
// Indica se o vencimento do bancarizador difere do vencimento da One7 (comparação por dia).
const vencimentoDivergente = row => {
  if (!row?.vencimento || !row?.vencimentoBancarizador) return false;
  const one7 = moment(row.vencimento);
  const banking = moment(row.vencimentoBancarizador);
  if (!one7.isValid() || !banking.isValid()) return false;
  return !one7.isSame(banking, 'day');
};
const initialPaginationModel = { page: 0, pageSize: 10 };

// Máximo de consultas simultâneas ao bancarizador (uma requisição por título)
const LIMITE_CONSULTAS_BANCARIZADOR = 3;

// Mesma regra do botão "Capturar" do detalhe: títulos já capturados/conciliados não são capturáveis.
const podeCapturar = row => !['Capturado', 'Conciliado'].includes(row?.status);

// Mensagem e tipo do aviso a partir do resumo da captura em lote (data.capturados/falhas/resultados).
const resumoCapturaLote = ({ capturados = 0, falhas = 0, resultados = [] } = {}) => {
  const primeiraFalha = resultados.find(resultado => !resultado.capturado)?.mensagem;
  const motivo = primeiraFalha ? ` Motivo: ${primeiraFalha}` : '';

  if (!falhas) return { tipo: 'success', mensagem: `${capturados} título(s) capturado(s) com sucesso.` };
  if (!capturados) return { tipo: 'error', mensagem: `Nenhum título foi capturado. ${motivo}` };
  return {
    tipo: 'warning',
    mensagem: `${capturados} título(s) capturado(s) e ${falhas} com falha. ${motivo}`
  };
};

const statusLabels = {
  disponivel: 'Disponível',
  em_conciliacao: 'Em conciliação',
  capturado: 'Capturado',
  conciliado: 'Conciliado',
  devolvido: 'Devolvido',
  aberto: 'Aberto',
  pago: 'Pago',
  baixado: 'Baixado',
  em_analise: 'Em Análise',
  encontrado: 'Encontrado',
  nao_encontrado: 'Não Encontrado',
  pago_a_maior: 'Pago a Maior',
  baixado_manualmente: 'Baixado',
  pendente: 'Pendente',
  problema: 'Problema'
};

const normalizeStatus = value => {
  if (!value) return null;
  const normalizedValue = String(value).trim().toLowerCase().replaceAll(' ', '_');
  return statusLabels[normalizedValue] || value;
};

const normalizeTipoLiquidacao = (value, paidBy) => {
  if (!value) return '';
  const normalizedPaidBy = paidBy === 'C' ? 'Cedente' : paidBy === 'S' ? 'Sacado' : '';
  return value === 'RC' ? `Recomprado ${normalizedPaidBy}` : '';
};

const mapTitulo = titulo => ({
  id: titulo.id,
  parceiro: titulo.name,
  cnpj: titulo.document_number,
  bordero: titulo.batch_number,
  ndoc: titulo.bill_number,
  vencimento: titulo.due_date,
  valor: titulo.amount,
  carteira: titulo.portfolio,
  liquidacaoData: titulo.settlement_date,
  liquidacaoTipo: normalizeTipoLiquidacao(titulo.reason, titulo.paid_by),
  status: normalizeStatus(titulo.current_status),
  operationOriginal: titulo.tenant_title_number,
  vencimentoParceiro: titulo.tenant_due_date,
  liquidacaoParceiro: titulo.tenant_settlement_date,
  statusTransferenciaParceiro: titulo.tenant_status_description,
  bancarizador: titulo.banking_name,
  situacao: normalizeStatus(titulo.banking_situation),
  statusTransferenciaBancarizador: normalizeStatus(titulo.banking_status),
  vencimentoBancarizador: titulo.banking_due_date,
  ourNumber: titulo.our_number,
  companyId: titulo.company_id,
  motivo: titulo.reason,
  pagoPor: titulo.paid_by,
  historico: []
});

// Campos do grupo "Bancarizador", preenchidos pela 2ª requisição (etapa bancarizador)
const bancarizadorFields = [
  'bancarizador',
  'situacao',
  'statusTransferenciaBancarizador',
  'vencimentoBancarizador'
];

// Mostra um loader na célula enquanto a 2ª requisição não completa a linha
const celulaBancarizador = render =>
  function CelulaBancarizador(p) {
    if (p.row.bancarizadorCarregando) {
      return <Skeleton variant="text" width="80%" aria-label="Carregando dados do bancarizador" />;
    }
    return render(p);
  };

const sortFields = {
  parceiro: 'name',
  bordero: 'batch_number',
  ndoc: 'bill_number',
  vencimento: 'due_date',
  valor: 'amount',
  carteira: 'portfolio',
  liquidacaoData: 'settlement_date',
  status: 'current_status',
  operationOriginal: 'tenant_title_number',
  vencimentoParceiro: 'tenant_due_date',
  liquidacaoParceiro: 'tenant_settlement_date',
  statusTransferenciaParceiro: 'tenant_status_description',
  bancarizador: 'banking_name',
  situacao: 'banking_situation',
  statusTransferenciaBancarizador: 'banking_status',
  vencimentoBancarizador: 'banking_due_date'
};

const buildGridParams = (filters, paginationModel, sortModel) => {
  const sort = sortModel?.[0];

  return {
    ...(filters.parceiros?.length ? { document_number: filters.parceiros } : {}),
    ...(filters.bordero ? { batch_number: filters.bordero } : {}),
    ...(filters.ndoc ? { bill_number: filters.ndoc } : {}),
    ...(filters.liquidacaoDe ? { settlement_date_start: filters.liquidacaoDe } : {}),
    ...(filters.liquidacaoAte ? { settlement_date_end: filters.liquidacaoAte } : {}),
    ...(filters.type ? { type: filters.type } : {}),
    ...(filters.dataDe ? { acceptance_date_start: filters.dataDe } : {}),
    ...(filters.dataAte ? { acceptance_date_end: filters.dataAte } : {}),
    ...(filters.statusFlow ? { tenant_status_description: filters.statusFlow } : {}),
    ...(filters.vencimentoParceiroDe ? { tenant_due_date_start: filters.vencimentoParceiroDe } : {}),
    ...(filters.vencimentoParceiroAte ? { tenant_due_date_end: filters.vencimentoParceiroAte } : {}),
    ...(filters.statusTransfer ? { status_transfer: filters.statusTransfer } : {}),
    ...(filters.situacao ? { banking_situation: filters.situacao } : {}),
    ...(filters.vencimentoBancarizadorDe
      ? { banking_due_date_start: filters.vencimentoBancarizadorDe }
      : {}),
    ...(filters.vencimentoBancarizadorAte
      ? { banking_due_date_end: filters.vencimentoBancarizadorAte }
      : {}),
    page: (paginationModel?.page || 0) + 1,
    per_page: paginationModel?.pageSize || 10,
    ...(sort?.field && sortFields[sort.field]
      ? { sort_by: sortFields[sort.field], sort_direction: sort.sort || 'asc' }
      : {})
  };
};

const RowActions = ({ row, lateral, onOpenActions, onOpenDetails }) => (
  <Stack spacing={0.5}>
    <Tooltip title="Ver detalhes">
      <IconButton size="small" onClick={() => onOpenDetails(row)} aria-label="Ver detalhes">
        <VisibilityOutlinedIcon />
      </IconButton>
    </Tooltip>
  </Stack>
);

export default function useConciliacaoFlow() {
  const [filters, setFilters] = useState(initialFilters);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState(initialPaginationModel);
  const [sortModel, setSortModel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [actionRow, setActionRow] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [parceiros, setParceiros] = useState([]);
  const [carregandoParceiros, setCarregandoParceiros] = useState(true);
  const [loadingBancarizador, setLoadingBancarizador] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [selecaoLoteAtiva, setSelecaoLoteAtiva] = useState(false);
  const [titulosSelecionados, setTitulosSelecionados] = useState([]);

  // Identifica a carga mais recente: respostas de cargas antigas (troca de página/filtro) são ignoradas
  const ultimaCargaRef = useRef(0);

  const load = useCallback(async (nextFilters, nextPaginationModel, nextSortModel) => {
    const cargaId = ++ultimaCargaRef.current;
    const params = buildGridParams(nextFilters, nextPaginationModel, nextSortModel);

    setLoading(true);
    setLoadingBancarizador(false);
    setError('');
    setTitulosSelecionados([]);

    // 1ª requisição: One7 + parceiro (rápida). A grid aparece com loader no bancarizador.
    let appointment = null;
    let idsTitulos = [];
    try {
      const result = await getConciliacaoFlowTitulosBase(params);
      if (cargaId !== ultimaCargaRef.current) return;

      appointment = result.appointment;
      const linhas = result.data.map(titulo => ({
        ...mapTitulo(titulo),
        bancarizadorCarregando: Boolean(titulo.id)
      }));
      idsTitulos = [...new Set(linhas.map(row => row.id).filter(Boolean))];
      setRows(linhas);
      setRowCount(result.rowCount);
    } catch (e) {
      if (cargaId === ultimaCargaRef.current) setError('Não foi possível carregar os títulos.');
      return;
    } finally {
      if (cargaId === ultimaCargaRef.current) setLoading(false);
    }

    if (!idsTitulos.length) return;

    // 2ª etapa: uma requisição por título para as colunas do bancarizador (Grafeno),
    // com no máximo LIMITE_CONSULTAS_BANCARIZADOR em paralelo. Cada linha é
    // atualizada assim que a resposta do seu título chega.
    const cargaAtual = () => cargaId === ultimaCargaRef.current;
    const atualizarLinha = (id, campos) =>
      setRows(prev =>
        prev.map(row => (row.id === id ? { ...row, ...campos, bancarizadorCarregando: false } : row))
      );

    let falhas = 0;
    const consultarTitulo = async id => {
      try {
        const result = await getConciliacaoFlowTitulosBancarizador(params, appointment, id);
        if (!cargaAtual()) return;

        const titulo = result.data.find(item => item.id === id);
        const completo = titulo ? mapTitulo(titulo) : null;
        atualizarLinha(
          id,
          completo
            ? {
                bancarizador: completo.bancarizador,
                situacao: completo.situacao,
                statusTransferenciaBancarizador: completo.statusTransferenciaBancarizador,
                vencimentoBancarizador: completo.vencimentoBancarizador
              }
            : {}
        );
      } catch (e) {
        if (!cargaAtual()) return;
        falhas += 1;
        atualizarLinha(id, {});
      }
    };

    const fila = [...idsTitulos];
    const processarFila = async () => {
      while (fila.length && cargaAtual()) {
        // eslint-disable-next-line no-await-in-loop
        await consultarTitulo(fila.shift());
      }
    };

    setLoadingBancarizador(true);
    await Promise.all(
      Array.from({ length: Math.min(LIMITE_CONSULTAS_BANCARIZADOR, fila.length) }, processarFila)
    );

    if (!cargaAtual()) return;
    setLoadingBancarizador(false);
    if (falhas) {
      openSnackbarMain(
        `Não foi possível carregar os dados do bancarizador de ${falhas} título(s).`,
        'warning'
      );
    }
  }, []);

  useEffect(() => {
    load(initialFilters, initialPaginationModel, []);
  }, [load]);

  useEffect(() => {
    let mounted = true;

    getParceirosConciliacao()
      .then(response => {
        const data = response?.data?.data || response?.data || [];
        if (!mounted) return;
        setParceiros(
          (Array.isArray(data) ? data : [])
            .map(parceiro => ({
              label: parceiro.name || '',
              value: parceiro.document_number || ''
            }))
            .filter(parceiro => parceiro.label && parceiro.value)
        );
      })
      .catch(() => {
        if (mounted) setParceiros([]);
      })
      .finally(() => {
        if (mounted) setCarregandoParceiros(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const action = useCallback((type, item) => {
    setMotivo('');
    setConfirm({ type, item });
  }, []);

  const alternarSelecaoLote = useCallback(() => {
    setTitulosSelecionados([]);
    setSelecaoLoteAtiva(ativa => !ativa);
  }, []);

  const abrirCapturaLote = useCallback(() => {
    if (!titulosSelecionados.length) return;
    setConfirm({ type: 'capturar_lote', itens: titulosSelecionados });
  }, [titulosSelecionados]);

  const confirmarCapturaLote = useCallback(async () => {
    setActionLoading(true);
    try {
      const response = await capturarTitulosEmLote(confirm.itens);
      const { tipo, mensagem } = resumoCapturaLote(response?.data?.data);
      openSnackbarMain(mensagem, tipo);
      setConfirm(null);
      await load(filters, paginationModel, sortModel);
    } catch (e) {
      const mensagem = e?.response?.data?.message || 'Não foi possível concluir a captura em lote.';
      openSnackbarMain(mensagem, 'error');
    } finally {
      setActionLoading(false);
    }
  }, [confirm, filters, load, paginationModel, sortModel]);

  const confirmAction = useCallback(async () => {
    if (confirm?.type === 'capturar_lote') {
      await confirmarCapturaLote();
      return;
    }

    setActionLoading(true);
    try {
      if (confirm.type === 'capturar') await capturarTitulo(confirm.item.id);
      else await devolverTitulo(confirm.item.id, motivo);
      openSnackbarMain(
        confirm.type === 'capturar' ? 'Título capturado com sucesso.' : 'Título devolvido com sucesso.',
        'success'
      );
      setConfirm(null);
      setSelected(null);
      await load(filters, paginationModel, sortModel);
    } catch (e) {
      const data = e?.response?.data;
      const mensagem = data?.message || 'Não foi possível concluir a operação.';
      openSnackbarMain(mensagem, 'error');
    } finally {
      setActionLoading(false);
    }
  }, [confirm, confirmarCapturaLote, filters, load, motivo, paginationModel, sortModel]);

  const stats = useMemo(() => {
    const totalValue = rows.reduce((sum, row) => sum + Number(row.valor || 0), 0);
    const available = rows.filter(row => row.status === 'Disponível').length;
    const captured = rows.filter(row => ['Capturado', 'Conciliado'].includes(row.status)).length;
    const attention = rows.filter(vencimentoDivergente).length;
    return { totalValue, available, captured, attention };
  }, [rows]);

  const filtersCount = Object.values(filters).reduce(
    (count, value) => count + (Array.isArray(value) ? (value.length ? 1 : 0) : value ? 1 : 0),
    0
  );

  const clearFilters = useCallback(() => {
    const clean = { ...initialFilters, parceiros: [] };
    const firstPage = { ...initialPaginationModel };
    setFilters(clean);
    setPaginationModel(firstPage);
    load(clean, firstPage, sortModel);
  }, [load, sortModel]);

  const applyFilters = useCallback(
    nextFilters => {
      const firstPage = { ...initialPaginationModel };
      setPaginationModel(firstPage);
      load(nextFilters, firstPage, sortModel);
    },
    [load, sortModel]
  );

  const handlePaginationModelChange = useCallback(
    nextPaginationModel => {
      setPaginationModel(nextPaginationModel);
      load(filters, nextPaginationModel, sortModel);
    },
    [filters, load, sortModel]
  );

  const handleSortModelChange = useCallback(
    nextSortModel => {
      const firstPage = { ...paginationModel, page: 0 };
      setSortModel(nextSortModel);
      setPaginationModel(firstPage);
      load(filters, firstPage, nextSortModel);
    },
    [filters, load, paginationModel]
  );

  const columns = useMemo(
    () => [
      {
        field: 'parceiro',
        headerName: 'Parceiro',
        minWidth: 160,
        flex: 1.35,
        headerClassName: 'group-header-one7',
        cellClassName: 'group-cell-one7',
        renderCell: p => (
          <Box className="tower-partner-cell">
            <Typography className="tower-partner-name" title={p.value || ''}>
              {p.value || '—'}
            </Typography>
            <Typography className="tower-partner-cnpj" title={p.row.cnpj || ''}>
              {p.row.cnpj ? formatarDocumento(String(p.row.cnpj)) : '—'}
            </Typography>
          </Box>
        )
      },
      {
        field: 'bordero',
        headerName: 'Nº Borderô',
        minWidth: 110,
        flex: 1,
        headerClassName: 'group-header-one7',
        cellClassName: 'group-cell-one7'
      },
      {
        field: 'ndoc',
        headerName: 'Nº Documento',
        minWidth: 110,
        flex: 1,
        headerClassName: 'group-header-one7',
        cellClassName: 'group-cell-one7'
      },
      {
        field: 'vencimento',
        headerName: 'Vencimento',
        minWidth: 120,
        flex: 1,
        headerClassName: 'group-header-one7',
        cellClassName: 'group-cell-one7',
        valueFormatter: p => date(p.value)
      },
      {
        field: 'valor',
        headerName: 'Valor',
        minWidth: 130,
        flex: 1,
        headerClassName: 'group-header-one7',
        cellClassName: 'group-cell-one7',
        valueFormatter: p => money(p.value)
      },
      {
        field: 'carteira',
        headerName: 'Carteira',
        minWidth: 125,
        flex: 0.9,
        headerClassName: 'group-header-one7',
        cellClassName: 'group-cell-one7'
      },
      {
        field: 'liquidacaoData',
        headerName: 'Liquidação',
        minWidth: 155,
        flex: 1.2,
        headerClassName: 'group-header-one7',
        cellClassName: 'group-cell-one7',
        renderCell: p => (
          <Stack alignItems="center" spacing={0.25}>
            <Typography variant="body3">{date(p.value)}</Typography>
            {p.row.liquidacaoTipo && (
              <Typography className="tower-liquidacao-tipo" variant="caption">
                {p.row.liquidacaoTipo}
              </Typography>
            )}
          </Stack>
        )
      },
      {
        field: 'status',
        headerName: 'Status transferência',
        minWidth: 165,
        flex: 1.2,
        headerClassName: 'group-header-one7',
        cellClassName: 'group-cell-one7',
        renderCell: p => statusChip(p.value)
      },
      {
        field: 'statusTransferenciaParceiro',
        headerName: 'Status Flow',
        minWidth: 125,
        flex: 1,
        sortable: false,
        headerClassName: 'group-header-parceiro',
        cellClassName: 'group-cell-parceiro',
        renderCell: p => statusChip(p.value)
      },
      {
        field: 'operationOriginal',
        headerName: 'Operação',
        minWidth: 135,
        flex: 0.9,
        sortable: false,
        headerClassName: 'group-header-parceiro',
        cellClassName: 'group-cell-parceiro',
        renderCell: p => (p.value ? <Chip label={p.value} size="small" /> : '—')
      },
      {
        field: 'vencimentoParceiro',
        headerName: 'Vencimento',
        minWidth: 120,
        flex: 1,
        sortable: false,
        headerClassName: 'group-header-parceiro',
        cellClassName: 'group-cell-parceiro',
        valueGetter: p => rowDate(p.row, 'vencimentoParceiro')
      },
      {
        field: 'liquidacaoParceiro',
        headerName: 'Liquidação',
        minWidth: 120,
        flex: 1,
        sortable: false,
        headerClassName: 'group-header-parceiro',
        cellClassName: 'group-cell-parceiro',
        valueFormatter: p => date(p.value)
      },
      {
        field: 'bancarizador',
        headerName: 'NOME',
        minWidth: 135,
        flex: 0.95,
        sortable: false,
        headerClassName: 'group-header-bancarizador',
        cellClassName: 'group-cell-bancarizador',
        renderCell: celulaBancarizador(p => p.value || '—')
      },
      {
        field: 'situacao',
        headerName: 'Situação',
        minWidth: 105,
        flex: 0.9,
        sortable: false,
        headerClassName: 'group-header-bancarizador',
        cellClassName: 'group-cell-bancarizador',
        renderCell: celulaBancarizador(p => statusChip(p.value))
      },
      {
        field: 'statusTransferenciaBancarizador',
        headerName: 'Status transferência',
        minWidth: 165,
        flex: 1.2,
        sortable: false,
        headerClassName: 'group-header-bancarizador',
        cellClassName: 'group-cell-bancarizador',
        renderCell: celulaBancarizador(p => statusChip(p.value))
      },
      {
        field: 'vencimentoBancarizador',
        headerName: 'Vencimento',
        minWidth: 120,
        flex: 1,
        sortable: false,
        headerClassName: 'group-header-bancarizador',
        cellClassName: 'group-cell-bancarizador',
        valueGetter: p => rowDate(p.row, 'vencimentoBancarizador'),
        renderCell: celulaBancarizador(p => {
          if (!vencimentoDivergente(p.row)) {
            return p.value;
          }
          return (
            <Tooltip
              title={`Vencimento divergente — One7: ${date(p.row.vencimento)} · Bancarizador: ${date(
                p.row.vencimentoBancarizador
              )}`}
              arrow
            >
              <Box component="span" className="vencimento-divergente">
                <span>{p.value}</span>
                <WarningAmberRoundedIcon
                  fontSize="inherit"
                  className="vencimento-divergente-icone"
                  aria-label="Vencimento divergente do vencimento One7"
                />
              </Box>
            </Tooltip>
          );
        })
      },
      {
        field: 'actions',
        headerName: 'Ações',
        minWidth: 92,
        flex: 0.7,
        sortable: false,
        filterable: false,
        cellClassName: 'group-cell-one7',
        renderCell: p => (
          <RowActions row={p.row} lateral onOpenActions={setActionRow} onOpenDetails={setSelected} />
        )
      }
    ],
    []
  );

  const columnGroupingModel = useMemo(
    () => [
      {
        groupId: 'One7',
        headerClassName: 'agrupamento group-header',
        children: [
          'parceiro',
          'bordero',
          'ndoc',
          'vencimento',
          'valor',
          'carteira',
          'liquidacaoData',
          'status'
        ].map(field => ({ field }))
      },
      {
        groupId: 'Parceiro',
        headerClassName: 'agrupamento group-header-parceiro',
        children: [
          'statusTransferenciaParceiro',
          'operationOriginal',
          'vencimentoParceiro',
          'liquidacaoParceiro'
        ].map(field => ({ field }))
      },
      {
        groupId: 'Bancarizador',
        headerClassName: 'agrupamento group-header-bancarizador',
        children: bancarizadorFields.map(field => ({ field }))
      }
    ],
    []
  );

  return {
    filters,
    setFilters,
    rows,
    rowCount,
    loading,
    loadingBancarizador,
    error,
    selected,
    setSelected,
    actionRow,
    setActionRow,
    confirm,
    setConfirm,
    motivo,
    setMotivo,
    load,
    action,
    confirmAction,
    actionLoading,
    filterOpen,
    setFilterOpen,
    stats,
    filtersCount,
    clearFilters,
    applyFilters,
    paginationModel,
    sortModel,
    handlePaginationModelChange,
    handleSortModelChange,
    columns,
    columnGroupingModel,
    parceiros,
    carregandoParceiros,
    vencimentoDivergente,
    selecaoLoteAtiva,
    alternarSelecaoLote,
    titulosSelecionados,
    setTitulosSelecionados,
    abrirCapturaLote,
    podeCapturar
  };
}
